import { useEffect, useState } from "react";
import { getSession, restoreSession } from "@/lib/session";
import IdentityBadge from "@/components/IdentityBadge";

/**
 * Mi6BrandBand — sticky top brand header for all dashboard views.
 *
 * Uses the OFFICIAL locked Mi6 Group + Attilius horizontal partner lockup
 * from /brand/mi6-attilius-horizontal.png — one canonical asset, no CSS
 * inversion or color manipulation. This is the locked brand mark.
 *
 * Layout:
 *   Left  : Official Mi6 Group + powered by ATTILIUS lockup (single image)
 *   Center: AIP · Revenue Lifecycle (small, hides on mobile)
 *   Right : Optional context label (passed via prop)
 *
 * Background: Soft off-white (#FAFAF7) — same tone as the page surface
 * for a seamless, less clinical look. The official lockup is designed for
 * light surfaces. A subtle bottom rule separates it from the page.
 *
 * Height: 64px desktop, 56px mobile.
 */

interface Mi6BrandBandProps {
  /** Optional small label rendered on the right side (e.g. "MOCK · Design Only"). */
  rightLabel?: string;
}

/** Return the role-aware home path for the logo click. */
function roleHomePath(): string {
  const s = getSession();
  if (!s) return "/#/dashboards";
  switch (s.role) {
    case "ae":        return "/#/dashboards/ae";
    case "bdr":       return "/#/dashboards/bdr";
    case "manager":   return s.isDualRole ? "/#/dashboards/executive" : "/#/dashboards/manager";
    case "executive": return "/#/dashboards/executive";
    case "marketing": return "/#/dashboards/marketing";
    case "se":        return "/#/dashboards/se";
    default:          return "/#/dashboards";
  }
}

export default function Mi6BrandBand({ rightLabel }: Mi6BrandBandProps) {
  // Show admin Support link only for manager / executive.
  const [showSupportLink, setShowSupportLink] = useState(false);
  const [logoHref, setLogoHref] = useState("/#/dashboards");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let s = getSession();
      if (!s) {
        try {
          s = await restoreSession();
        } catch {
          /* not signed in is fine */
        }
      }
      if (cancelled) return;
      const role = String(s?.role ?? "").toLowerCase();
      setShowSupportLink(role === "manager" || role === "executive");
      // Set logo href based on role
      setLogoHref(roleHomePath());
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="w-full flex items-center justify-between px-4 sm:px-6 sticky top-0 z-50 select-none"
      style={{
        background: "#FAFAF7",
        height: "64px",
        borderBottom: "1px solid #E5E5E7",
        boxShadow: "0 1px 0 rgba(15, 23, 42, 0.02)",
      }}
    >
      {/* ── Left: OFFICIAL locked Mi6 + Attilius partner lockup ────────── */}
      <a
        href={logoHref}
        className="flex items-center"
        aria-label="Mi6 Group · Powered by Attilius"
        style={{ textDecoration: "none" }}
      >
        <img
          src="/brand/mi6-attilius-horizontal.png"
          alt="Mi6 Group · Powered by Attilius"
          style={{
            height: "36px",
            width: "auto",
            display: "block",
          }}
        />
      </a>

      {/* ── Center: AIP product label (hides on mobile) ─────────────────── */}
      <div
        className="hidden md:flex items-center gap-1.5"
        style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}
      >
        <span
          style={{
            fontSize: "10px",
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            color: "#6B6B70",
            fontWeight: 700,
          }}
        >
          Attilius
        </span>
        <span
          style={{
            display: "inline-block",
            width: "4px",
            height: "4px",
            borderRadius: "50%",
            background: "#DC2626",
          }}
        />
        <span
          style={{
            fontSize: "10px",
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            color: "#6B6B70",
            fontWeight: 700,
          }}
        >
          Revenue Lifecycle
        </span>
      </div>

      {/* ── Right: Identity badge + Mi6 Morning Brief + optional context label ─── */}
      <div className="flex items-center gap-2 sm:gap-3">
        <IdentityBadge />
        {showSupportLink && (
          <a
            href="/#/admin/support"
            style={{
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: "#0B0B0F",
              textDecoration: "none",
              padding: "4px 10px",
              borderRadius: "6px",
              background: "#0B0B0F0a",
              border: "1px solid #0B0B0F18",
              whiteSpace: "nowrap",
            }}
          >
            Support
          </a>
        )}
        {showSupportLink && (
          <a
            href="/activity/audit"
            style={{
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: "#0B0B0F",
              textDecoration: "none",
              padding: "4px 10px",
              borderRadius: "6px",
              background: "#0B0B0F0a",
              border: "1px solid #0B0B0F18",
              whiteSpace: "nowrap",
            }}
          >
            Activity Audit
          </a>
        )}
        <a
          href="/#/today"
          style={{
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "0.06em",
            color: "#0B0B0F",
            textDecoration: "none",
            padding: "4px 10px",
            borderRadius: "6px",
            background: "#0B0B0F0a",
            border: "1px solid #0B0B0F18",
            whiteSpace: "nowrap",
          }}
        >
          Mi6 Morning Brief
        </a>
        {rightLabel && (
          <span
            style={{
              fontSize: "9px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#8E8E94",
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {rightLabel}
          </span>
        )}
      </div>
    </div>
  );
}
